import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Label } from '@agconnections/grow-ui';
import useDebounce from 'hooks/useDebounce';
import usePestData from 'hooks/usePestData';
import SearchIcon from 'assets/search.svg?react';

const TargetPestAutocomplete = ({ pest = undefined, onSelectPest }) => {
  const [inputValue, setInputValue] = useState(pest?.name || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const debouncedSearch = useDebounce(inputValue, 300);
  const containerRef = useRef();

  const { pests, loading } = usePestData(inputValue ? debouncedSearch : '');

  const pestOptions = useMemo(() => {
    if (!inputValue.trim()) return [];
    return (
      (pests || [])?.map(p => ({
        pestid: p.pestid,
        nameid: p.nameid,
        name: p.name,
        latinNames: p.latinnames
      })) || []
    );
  }, [pests, inputValue]);

  useEffect(() => {
    if (pest?.name) {
      setInputValue(pest.name);
    }
  }, [pest]);

  const handleSelect = selected => {
    setInputValue(selected.name);
    setShowDropdown(false);
    onSelectPest(selected);
  };

  const handleClear = () => {
    setInputValue('');
    setShowDropdown(false);
    onSelectPest(undefined);
  };

  const handleClickOutside = e => {
    if (!containerRef.current?.contains(e.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative target-pest-field">
      <div className="-mt-1 mb-1">
        <Label>Target Pest</Label>
      </div>
      <div className="relative w-full">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
          <SearchIcon className="w-5 h-5" />
        </span>
        <input
          className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md outline-hidden focus:ring focus:border-blue-400 placeholder-gray-500"
          placeholder="Search pests"
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            setShowDropdown(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setShowDropdown(true)}
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
          >
            ✕
          </button>
        )}
      </div>
      {showDropdown && (
        <ul className="absolute z-50 max-h-200px mt-1 w-full overflow-auto bg-white border border-gray-300 rounded shadow-lg">
          <li className="px-3 py-2 text-sm text-gray-500 border-b border-gray-200 flex justify-between items-center">
            <span className="italic">{loading ? 'Loading...' : ''}</span>
            {!loading && (
              <span className="text-xs text-gray-400">
                {pestOptions.length} result{pestOptions.length !== 1 && 's'}
              </span>
            )}
          </li>
          {!loading &&
            pestOptions?.map((_pest, index) => (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                key={_pest.pestid}
                onMouseDown={() => handleSelect(_pest)}
                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                  index === highlightedIndex ? 'bg-blue-100' : ''
                }`}
              >
                {_pest.name}
              </li>
            ))}
          {!loading && pestOptions.length === 0 && (
            <li className="px-3 py-2 text-gray-500 italic">No pests found</li>
          )}
        </ul>
      )}
    </div>
  );
};

TargetPestAutocomplete.propTypes = {
  pest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }),
  onSelectPest: PropTypes.func.isRequired
};

export default TargetPestAutocomplete;
